import React, { useMemo } from "react";

import {
  INVALID_CHARS,
  INVALID_CHARS_ESCAPED,
  buildFieldFormatValidator,
} from "../../Constant";
import ErrorCheckingAutocompleteInput from "../../ReusableComponents/ErrorCheckingAutocomplete";

import { removeReplicateTag } from "../../helpers";

import MinusIconPng from "../../../../../assets/images/sample-submission/FieldsMinusIcon.png";

export default function SampleDetailsAutocompleteInput({
  setFieldValue,
  handleDeselectField,
  inputValues,
  setInputValuesRefs,
  json_field,
  idx,
  sampleFieldsInfo,
  delimiterRegex,
  setFieldInputInvalid,
  charLimit,
  dropdownSuggestionsObj,
  sanitizeDropdown,
  sampleIdFields,
  isCompositeSample,
  inputInvalid,
}) {
  const invalidRegexArr = [...INVALID_CHARS_ESCAPED];
  if (delimiterRegex && json_field !== "sample_id" && sampleIdFields.has(json_field)) { // if not empty string
    invalidRegexArr.push(delimiterRegex);
  }

  /** Get field's suggestions for the dropdown list */
  const dropdownList = useMemo(() => {
    if (dropdownSuggestionsObj[json_field] && dropdownSuggestionsObj[json_field].length) {
      return sanitizeDropdown(dropdownSuggestionsObj[json_field], true);
    }
    return [];
  }, [dropdownSuggestionsObj, json_field, sanitizeDropdown]);

  const fieldInfo = sampleFieldsInfo[json_field] || {};
  const labelRequired = fieldInfo.mandatory === "1";

  const { formatValidator, formatPlaceholder } = useMemo(() => {
    const fieldFormat = fieldInfo.field_format;

    if (fieldFormat && fieldFormat.type && fieldFormat.format) {
      const type = String(fieldFormat.type).toLowerCase();
      if (type === "date" || type === "time") {
        const { validate, placeholder } = buildFieldFormatValidator(fieldFormat.format, type);
        return {
          formatValidator: validate,
          formatPlaceholder: placeholder,
        };
      }
    }

    if (fieldFormat && fieldFormat.type) {
      const type = String(fieldFormat.type).toLowerCase();
      if (type === "fixed_values") {
        if (isCompositeSample) {
          return { formatValidator: null, formatPlaceholder: null };
        }
        const normalizedOptions = new Set(
          dropdownList.map((option) => option?.value?.trim()?.toLowerCase()).filter(Boolean),
        );
        return {
          formatValidator: (value) => {
            if (!value || value.trim() === "" || normalizedOptions.size === 0) {
              return { isValid: true, errorMessage: null };
            }
            const isValid = normalizedOptions.has(value.trim().toLowerCase());
            return {
              isValid,
              errorMessage: isValid ? null : "Please select a value from dropdown options",
            };
          },
          formatPlaceholder: null,
        };
      }
    }

    return { formatValidator: null, formatPlaceholder: null };
  }, [fieldInfo, dropdownList, isCompositeSample]);

  /** On option click in autocomplete dropdown, set value in parent, auto fill product_name and tests
   * @param {String} selectedOption value of autocomplete option
   */
  const handleSelectOption = (selectedOption) => {
    setFieldValue(json_field, selectedOption.trim(), true);
  };

  /**
   * On change, set value of field in parent
   */
  const onChange = (value) => {
    if (value !== inputValues[json_field]) {
      setFieldValue(json_field, value); // Don't use the trimmed value here to avoid the bad ux of whitespace being removed when the user is in the middle of typing. The input value will be trimmed before api calls (except for autofill)
    }
  };

  return (
    <div className="input-checkbox-row">
      <div className="sample-details-input">
        <ErrorCheckingAutocompleteInput
          ref={(input) => setInputValuesRefs(json_field, input)}
          label={sampleFieldsInfo[json_field].title_field}
          labelDivClassName="sample-details-input-title"
          inputID={`${json_field}-input`}
          value={(inputValues[json_field]) ?? ""}
          suggestions={dropdownList}
          invalidChars={invalidRegexArr}
          invalidKeys={INVALID_CHARS}
          handleSelectOption={handleSelectOption}
          onChange={onChange}
          hasExternalError={Boolean(inputInvalid[json_field])}
          onErrorChange={(inputErrorSet) => setFieldInputInvalid(json_field, inputErrorSet.size > 0)}
          charLimit={charLimit}
          handleSanitizeInputOnAutoFill={(val) => removeReplicateTag(val)}
          formatValidator={formatValidator}
          formatPlaceholder={formatPlaceholder}
          labelRequired={labelRequired}
        />
        <img
          onClick={() => {
            if (labelRequired) return;
            handleDeselectField(idx, json_field);
          }}
          onKeyDown={(e) => {
            if (labelRequired) return;
            if (e.key === "Enter" || e.key === " ") {
              handleDeselectField(idx, json_field);
            }
          }}
          tabIndex={labelRequired ? -1 : 0} //eslint-disable-line
          src={MinusIconPng}
          alt="field minus icon"
          className="sample-field-minus-icon"
          style={labelRequired ? { visibility: "hidden", pointerEvents: "none" } : undefined}
          aria-hidden={labelRequired ? "true" : undefined}
        />
      </div>
      {/*
        {!hideSaveForNext && (
          !isCompositeSample && (
          <motion.div className="sample-submission-checkbox-container">
            <input type="checkbox" checked={inputValuesSavedForNext[json_field] ?? false} onChange={() => { toggleSaveForNext(json_field); }} />
            <span className="sample-submission-checkmark" onClick={() => { toggleSaveForNext(json_field); }} />
          </motion.div>
          )
        )}
      */}
    </div>
  );
}
